// OIDC service for fwc-react-esri-keycloak
// Based on the working implementation from test-login-fwatools


// Simplified OIDC configuration without PKCE
const LOGGED_OUT_KEY = "oidc.logged_out";

/** Resolve app base URL from PUBLIC_URL (path or absolute) + current origin. */
const resolveAppBaseUrl = (): string => {
  const raw = process.env.PUBLIC_URL || "";
  let path = "";
  if (/^https?:\/\//i.test(raw)) {
    try {
      path = new URL(raw).pathname;
    } catch {
      path = "";
    }
  } else {
    path = raw;
  }
  path = path.replace(/\/+$/, "");
  // Infer /FWC when PUBLIC_URL was mis-set to origin-only and we are under /FWC
  if (!path && window.location.pathname.startsWith("/FWC")) {
    path = "/FWC";
  }
  return `${window.location.origin}${path}`.replace(/\/+$/, "");
};

const keycloakBase = (process.env.REACT_APP_KEYCLOAK_URL || "").replace(
  /\/+$/,
  ""
);

const getOidcConfig = () => {
  const baseUrl = resolveAppBaseUrl();
  return {
    client_id: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || "web",
    redirect_uri: `${baseUrl}/callback`,
    response_type: "code",
    scope: "openid profile email offline_access",
    // Must match Keycloak Valid Post Logout Redirect URIs (no trailing slash)
    post_logout_redirect_uri: baseUrl,
    pkce: false,
    authorization_endpoint: `${keycloakBase}/realms/${process.env.REACT_APP_KEYCLOAK_REALM}/protocol/openid-connect/auth`,
    token_endpoint: `${keycloakBase}/realms/${process.env.REACT_APP_KEYCLOAK_REALM}/protocol/openid-connect/token`,
    userinfo_endpoint: `${keycloakBase}/realms/${process.env.REACT_APP_KEYCLOAK_REALM}/protocol/openid-connect/userinfo`,
    end_session_endpoint: `${keycloakBase}/realms/${process.env.REACT_APP_KEYCLOAK_REALM}/protocol/openid-connect/logout`,
  };
};

export const markLoggedOut = () => {
  try {
    localStorage.setItem(LOGGED_OUT_KEY, "1");
    sessionStorage.setItem(LOGGED_OUT_KEY, "1");
  } catch (e) {
    console.warn("Could not persist logged-out flag", e);
  }
};

export const clearLoggedOut = () => {
  try {
    localStorage.removeItem(LOGGED_OUT_KEY);
    sessionStorage.removeItem(LOGGED_OUT_KEY);
  } catch (e) {
    /* ignore */
  }
};

export const isLoggedOutLanding = (): boolean => {
  try {
    if (localStorage.getItem(LOGGED_OUT_KEY) === "1") return true;
    if (sessionStorage.getItem(LOGGED_OUT_KEY) === "1") return true;
  } catch (e) {
    /* ignore */
  }
  return false;
};

// Global flags to prevent duplicate callback processing / stacked navigations
let isProcessingCallback = false;
let isSigningIn = false;
let isSigningOut = false;

export const resetAuthNavigation = () => {
  isSigningIn = false;
  isSigningOut = false;
};

/** True while Keycloak logout redirect is in flight or after logout landing. */
export const isLogoutInProgress = (): boolean =>
  isSigningOut || isLoggedOutLanding();

// Create a simple OIDC manager that doesn't require server validation
const userManager = {
  getUser: async () => {
    console.log("=== GET USER CALLED ===");
    console.log("Getting user from localStorage...");
    try {
      const userStr = localStorage.getItem("oidc.user");
      
      if (userStr) {
        const user = JSON.parse(userStr);
        // Check if token is expired (with 30s buffer so we re-auth before expiry)
        const now = Math.floor(Date.now() / 1000);
        
        if (user.expires_at && user.expires_at <= now + 30) {
          console.log("User access token is expired or expiring soon");
          
          if (user.refresh_token) {
             console.log("Attempting to refresh token...");
             const refreshedUser = await userManager.refreshToken(user.refresh_token);
             if (refreshedUser) {
               return refreshedUser;
             }
          }
          
          console.log("Token refresh failed or no refresh token, removing from localStorage");
          localStorage.removeItem("oidc.user");
          // Notify application of logout to clear other tokens (Esri, etc.)
          window.dispatchEvent(new Event("oidc-auth-logout"));
          return null;
        }
        
        console.log("User token is valid, returning user");
        return user;
      }
      console.log("No user found in localStorage");
      return null;
    } catch (error) {
      console.error("Error getting user:", error);
      return null;
    }
  },
  
  refreshToken: async (refreshToken: string) => {
    console.log("=== REFRESH TOKEN CALLED ===");
    try {
      const oidcConfig = getOidcConfig();
      const tokenParams = {
        grant_type: "refresh_token",
        client_id: oidcConfig.client_id,
        refresh_token: refreshToken,
      };

      const tokenResponse = await fetch(oidcConfig.token_endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(tokenParams),
      });

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        console.error("Token refresh failed:", tokenResponse.status, errorText);
        return null;
      }

      const tokenData = await tokenResponse.json();
      console.log("Token refresh successful");

      // Get existing user to preserve profile info
      const userStr = localStorage.getItem("oidc.user");
      const existingUser = userStr ? JSON.parse(userStr) : {};

      const newUser = {
        ...existingUser,
        access_token: tokenData.access_token,
        refresh_token: tokenData.refresh_token || existingUser.refresh_token, // Use new refresh token if provided
        id_token: tokenData.id_token || existingUser.id_token,
        expires_at: Math.floor(Date.now() / 1000) + (tokenData.expires_in || 300),
      };

      localStorage.setItem("oidc.user", JSON.stringify(newUser));
      
      // Notify application of auth update
      window.dispatchEvent(new Event("oidc-auth-update"));
      
      return newUser;
    } catch (error) {
       console.error("Error refreshing token:", error);
       return null;
    }
  },

  signinRedirect: () => {
    console.log("OIDC signinRedirect called (without PKCE)");
    console.log("Current window location:", window.location.href);

    // Logout wins — never start login while Keycloak logout redirect is in flight
    if (isSigningOut) {
      console.log("Logout in progress, skipping login redirect");
      return;
    }
    if (isSigningIn) {
      console.log("Login navigation already in progress, skipping duplicate redirect");
      return;
    }
    isSigningIn = true;
    clearLoggedOut();

    const oidcConfig = getOidcConfig();
    const state = Math.random().toString(36).substring(7);
    const loginUrl =
      `${oidcConfig.authorization_endpoint}?` +
      `client_id=${oidcConfig.client_id}&` +
      `redirect_uri=${encodeURIComponent(oidcConfig.redirect_uri)}&` +
      `response_type=${oidcConfig.response_type}&` +
      `scope=${encodeURIComponent(oidcConfig.scope)}&` +
      `state=${state}`;
    console.log("Redirecting to login:", loginUrl);
    window.location.replace(loginUrl);
  },

  signoutRedirect: () => {
    console.log("OIDC signoutRedirect called");
    // Cancel any pending login intent — logout must navigate to Keycloak
    isSigningOut = true;
    isSigningIn = false;

    // IMPORTANT: read id_token BEFORE any UI code clears localStorage
    const oidcConfig = getOidcConfig();
    let idToken: string | undefined;
    try {
      const userStr = localStorage.getItem("oidc.user");
      if (userStr) {
        idToken = JSON.parse(userStr)?.id_token;
      }
    } catch (e) {
      console.warn("Could not read id_token before logout", e);
    }

    localStorage.removeItem("oidc.user");
    localStorage.removeItem("processed_code");
    localStorage.removeItem("esriToken");
    localStorage.removeItem("esriTokenExpiry");
    markLoggedOut();

    const logoutParams = new URLSearchParams({
      client_id: oidcConfig.client_id,
      post_logout_redirect_uri: oidcConfig.post_logout_redirect_uri,
    });
    if (idToken) {
      logoutParams.set("id_token_hint", idToken);
    } else {
      console.warn(
        "No id_token_hint for Keycloak logout — session end may hang or ask for confirm"
      );
    }
    const logoutUrl = `${oidcConfig.end_session_endpoint}?${logoutParams.toString()}`;
    console.log("Redirecting to Keycloak logout:", logoutUrl);
    if (idToken) {
      console.log("Logout includes id_token_hint: yes");
    }

    // Navigate FIRST — do not dispatch React logout events before this.
    // MapView.destroy() on auth clear was blocking the main thread and freezing UI.
    window.location.href = logoutUrl;

    // Fallback: if Keycloak navigation is blocked/slow, land on local logged-out UI
    window.setTimeout(() => {
      if (isSigningOut) {
        console.warn(
          "Keycloak logout navigation still pending — falling back to local logged-out page"
        );
        window.location.replace(oidcConfig.post_logout_redirect_uri);
      }
    }, 2000);
  },

  signinRedirectCallback: async () => {
    console.log("=== OIDC CALLBACK FUNCTION CALLED ===");
    console.log("OIDC signinRedirectCallback called (without PKCE)");
    
    // Prevent duplicate processing
    if (isProcessingCallback) {
      console.log("Callback already being processed, skipping...");
      return Promise.resolve(null);
    }

    isProcessingCallback = true;
    const oidcConfig = getOidcConfig();
    
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const iss = urlParams.get("iss");

    if (code) {
      console.log("Authorization code found, exchanging for access token...");

      // Check if we've already processed this code
      const processedCode = localStorage.getItem("processed_code");
      if (processedCode === code) {
        console.log("This authorization code has already been processed");
        const existingUser = localStorage.getItem("oidc.user");
        if (existingUser) {
          isProcessingCallback = false;
          return Promise.resolve(JSON.parse(existingUser));
        }
      }

      try {
        // Exchange authorization code for access token
        const tokenParams = {
          grant_type: "authorization_code",
          client_id: oidcConfig.client_id,
          code: code,
          redirect_uri: oidcConfig.redirect_uri,
        };

        const tokenResponse = await fetch(oidcConfig.token_endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(tokenParams),
        });

        if (!tokenResponse.ok) {
          const errorText = await tokenResponse.text();
          throw new Error(`Token exchange failed: ${tokenResponse.status} ${errorText}`);
        }

        const tokenData = await tokenResponse.json();
        console.log("Token exchange successful");

        // Get user info using the access token
        let userInfo = null;
        if (tokenData.access_token) {
          try {
            const userInfoResponse = await fetch(oidcConfig.userinfo_endpoint, {
              headers: {
                Authorization: `Bearer ${tokenData.access_token}`,
              },
            });

            if (userInfoResponse.ok) {
              userInfo = await userInfoResponse.json();
            }
          } catch (error) {
            console.warn("Error fetching user info:", error);
          }
        }

        // Create user object with real token data
        const user = {
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
          id_token: tokenData.id_token,
          expires_at:
            Math.floor(Date.now() / 1000) + (tokenData.expires_in || 300),
          profile: userInfo || {
            sub: "user",
            preferred_username: "user",
            email: "user@example.com",
            name: "User",
            iss:
              iss ||
              `${keycloakBase}/realms/${process.env.REACT_APP_KEYCLOAK_REALM}`,
          },
        };

        // Store user in localStorage
        localStorage.setItem("oidc.user", JSON.stringify(user));
        localStorage.setItem("processed_code", code); // Mark code as processed
        clearLoggedOut();
        
        isProcessingCallback = false; // Reset flag
        return Promise.resolve(user);
      } catch (error) {
        console.error("Error exchanging authorization code:", error);
        isProcessingCallback = false; // Reset flag on error
        return Promise.reject(error);
      }
    }

    console.error("No authorization code found in URL");
    isProcessingCallback = false; // Reset flag
    return Promise.reject(new Error("No authorization code received"));
  },
};

console.log("Simple OIDC manager created successfully");
export default userManager;
